import { SOCIAL_ERROR_MESSAGES, SocialSignInError, socialEmailFromFragment, type SocialProvider } from '@chapsi/contracts';

/**
 * Continue with Google or Microsoft on the web and in the desktop app (docs/adr/0081). Pure, so it is unit tested;
 * the sign-in screen does the calls.
 */

export interface SocialNotice {
  code: SocialSignInError;
  message: string;
  /** For `no_account`: the address the provider verified, to fill in for the emailed sign-up link. */
  email: string | null;
}

/** What a failed provider sign-in left on the address (`?social_error=<code>#email=…`), or null. */
export function socialNoticeFrom(search: string, hash: string): SocialNotice | null {
  const raw = new URLSearchParams(search).get('social_error');
  if (raw === null) return null;
  const known = SocialSignInError.safeParse(raw);
  const code = known.success ? known.data : 'failed';
  return { code, message: SOCIAL_ERROR_MESSAGES[code], email: code === 'no_account' ? socialEmailFromFragment(hash) : null };
}

/** What the desktop shell handed back: a code to trade, or our reason (with the address for `no_account`). */
export function socialNoticeFromDesktop(back: { code?: string; error?: string; email?: string }): { kind: 'code'; code: string } | ({ kind: 'notice' } & SocialNotice) {
  if (!back.error && back.code && /^[A-Za-z0-9_-]{20,200}$/.test(back.code)) return { kind: 'code', code: back.code };
  const known = SocialSignInError.safeParse(back.error);
  const code = known.success ? known.data : back.error ? 'failed' : 'expired';
  const email = code === 'no_account' && back.email ? socialEmailFromFragment(`#email=${encodeURIComponent(back.email)}`) : null;
  return { kind: 'notice', code, message: SOCIAL_ERROR_MESSAGES[code], email };
}

/**
 * The providers to show: the server's, unless this is a desktop shell that cannot bring a sign-in back to its window
 * (the provider's page opens in the default browser, which would sign in the browser instead).
 */
export function providersToShow(providers: readonly SocialProvider[] | undefined, env: { desktop: boolean; desktopCanReturn: boolean }): SocialProvider[] {
  if (!providers?.length) return [];
  if (env.desktop && !env.desktopCanReturn) return [];
  return [...providers];
}
